import { useFormik } from 'formik';
import type React from 'react';
import { useState, useMemo, useCallback } from 'react';
import * as Yup from 'yup';
import { useAccountRouteHelper } from '@apps/account/useAccountHelper';
import type { JoinEventAsPersonWithPasswordPayload } from '@graphql/generated-base';
import { useJoinEventAsPersonWithPasswordMutation } from '@graphql/mutations/joinEvent.graphql.generated';
import { useRegistryGuestTranslations } from '../../GuestRegistry.i18n';
import { useTranslation } from '@shared/core';
import { reportJoinEventError } from '@shared/utils/reportJoinEventError';

export interface RegistryProtectedFields extends Readonly<{ password: string }> {}

export const useRegistryProtectedController = (eventId: string = '') => {
  const [showContactHost, setShowContactHost] = useState(false);
  const [joinEvent, { loading: joinEventLoading }] = useJoinEventAsPersonWithPasswordMutation();
  const { getRegistryProtectedTranslations } = useRegistryGuestTranslations();
  const { incorrectPass } = getRegistryProtectedTranslations();
  const { tq } = useTranslation('generalFormValidation');

  const registryProtectedFormik = useFormik({
    initialValues: {
      password: '',
    },
    validationSchema: Yup.object<RegistryProtectedFields>({
      password: Yup.string()
        .trim()
        .required(tq`Required`),
    }),
    onSubmit: () => {},
  });

  const joinEventWithPassword = useCallback(
    async (eventId: string, payload: JoinEventAsPersonWithPasswordPayload) => {
      try {
        await joinEvent({ variables: { eventId, payload }, refetchQueries: () => ['CheckCanAccessPage'], awaitRefetchQueries: true });
      } catch (error) {
        reportJoinEventError(error, { context: 'RegistryProtected', operation: 'joinEventWithPassword', eventId });
        registryProtectedFormik.setFieldError('password', incorrectPass);
      }
    },
    [joinEvent, registryProtectedFormik, incorrectPass],
  );

  const { signInAndReturnToCurrentPath } = useAccountRouteHelper();

  const handleSubmit = useMemo(
    () => (e: React.SyntheticEvent) => {
      e.preventDefault();
      const trimmedPassword = registryProtectedFormik.values.password.trim();
      // Mark touched so both Required validation and server-side setFieldError
      // results (e.g. NO_VALID_PASSWORD_PROVIDED) are revealed on Enter-key submit
      // paths that never blur the field.
      registryProtectedFormik.setFieldTouched('password', true);
      if (!trimmedPassword) {
        // Skip the mutation — Yup's required validation surfaces "Required" instead.
        return;
      }
      joinEventWithPassword(eventId, { eventPassword: trimmedPassword });
    },
    [eventId, joinEventWithPassword, registryProtectedFormik],
  );

  const handleSigninClick = useMemo(
    () => async () => {
      signInAndReturnToCurrentPath();
    },
    [signInAndReturnToCurrentPath],
  );

  return {
    showContactHost,
    setShowContactHost,
    registryProtectedFormik,
    handleSubmit,
    handleSigninClick,
    joinEventLoading,
  };
};
